import { Pencil, X, Save, RefreshCcw, Loader2 } from "lucide-react";

function DetailHeader({ detail, onClose }) {
  return (
    <div className="flex items-start justify-between gap-4 mb-4">
      <div>
        <h2 className="text-lg font-semibold text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif" }}>
          {detail.content?.Descricao}
        </h2>
        <p className="text-xs text-[#978D78]">ID {detail.id} • {detail.content?.ID_TipoConteudoMediaCenter}</p>
      </div>
      <button onClick={onClose} className="text-[#978D78] hover:text-[#0F1829]">✕</button>
    </div>
  );
}

function ViewMode({ detail, onStartEdit }) {
  const tags = [detail.content?.tag1, detail.content?.tag2, detail.content?.tag3].filter(Boolean);
  return (
    <>
      <div className="flex items-start justify-between gap-2">
        <p className="text-[10px] uppercase tracking-wider text-[#978D78]">
          Resumo actual em SQL ({detail.content?.resumo_len || 0} / 950 chars)
        </p>
        <button onClick={onStartEdit} data-testid="ms-detail-edit-start"
          className="inline-flex items-center gap-1 text-xs text-[#0F1829] hover:underline">
          <Pencil size={12} /> Editar manualmente
        </button>
      </div>
      <div className="p-3 bg-[#F5F2EB] rounded text-sm text-[#0F1829] whitespace-pre-wrap">
        {detail.content?.resumo || <span className="text-[#978D78] italic">(vazio)</span>}
      </div>
      {tags.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-wider text-[#978D78] mb-1">Tags</p>
          <div className="flex flex-wrap gap-2">
            {tags.map((t, i) => (
              <span key={`detail-tag-${i}`}
                className="inline-block px-3 py-1 rounded-full bg-[#0F1829] text-white text-xs">
                {t}
              </span>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function EditMode({ editForm, updateEditField, save, saving, cancel }) {
  const overflow = editForm.resumo.length > 950;
  return (
    <>
      <div>
        <div className="flex items-center justify-between mb-1">
          <p className="text-[10px] uppercase tracking-wider text-[#978D78]">Resumo</p>
          <p className={`text-[10px] ${overflow ? "text-red-600" : "text-[#978D78]"}`}>
            {editForm.resumo.length} / 950
          </p>
        </div>
        <textarea value={editForm.resumo}
          onChange={(e) => updateEditField("resumo", e.target.value)}
          rows={8} data-testid="ms-edit-resumo"
          className="w-full px-3 py-2 text-sm border border-[#E6E1D6] rounded focus:ring-2 focus:ring-[#0F1829] outline-none resize-y" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[1, 2, 3].map((n) => (
          <div key={`edit-tag-${n}`}>
            <p className="text-[10px] uppercase tracking-wider text-[#978D78] mb-1">
              Tag {n} ({editForm[`tag${n}`].length}/100)
            </p>
            <input value={editForm[`tag${n}`]}
              onChange={(e) => updateEditField(`tag${n}`, e.target.value)}
              maxLength={100} data-testid={`ms-edit-tag${n}`}
              className="w-full px-3 py-2 text-sm border border-[#E6E1D6] rounded focus:ring-2 focus:ring-[#0F1829] outline-none" />
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 pt-1">
        <button onClick={save} disabled={saving || overflow} data-testid="ms-edit-save"
          className="px-3 py-2 bg-[#0F1829] text-white rounded text-xs inline-flex items-center gap-1.5 disabled:opacity-50 hover:bg-[#1a2542]">
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
          Guardar alterações
        </button>
        <button onClick={cancel} data-testid="ms-edit-cancel"
          className="px-3 py-2 border border-[#E6E1D6] text-[#0F1829] rounded text-xs inline-flex items-center gap-1.5 hover:bg-[#F5F2EB]">
          <X size={12} /> Cancelar
        </button>
      </div>
    </>
  );
}

function CacheInfo({ cache }) {
  if (!cache?.transcript_len) return null;
  return (
    <div className="text-xs text-[#6B7280]">
      Transcrição em cache: {cache.transcript_len.toLocaleString()} caracteres •
      modelo Whisper • LLM {cache.model_llm || "gemini-3-flash-preview"} •
      actualizado {cache.updated_at?.slice(0, 16)?.replace("T", " ")}
    </div>
  );
}

export default function SummaryDetailModal({
  detail, close,
  editing, editForm, updateEditField, startEdit, cancelEdit, saving, save,
  onRegenerate, isRegenerating,
}) {
  if (!detail) return null;
  return (
    <div className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center p-4" onClick={close}>
      <div data-testid="ms-detail-modal"
        className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}>
        <DetailHeader detail={detail} onClose={close} />
        <div className="space-y-4">
          {!editing
            ? <ViewMode detail={detail} onStartEdit={startEdit} />
            : <EditMode editForm={editForm} updateEditField={updateEditField}
                save={save} saving={saving} cancel={cancelEdit} />}
          <CacheInfo cache={detail.cache} />
          {detail.cache?.error && (
            <div className="p-3 bg-red-50 border border-red-200 text-sm text-red-800 rounded">
              <strong>Erro:</strong> {detail.cache.error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2 border-t border-[#F5F2EB]">
            <button onClick={() => onRegenerate(detail.id, true)} disabled={isRegenerating}
              data-testid="ms-detail-regenerate"
              className="px-3 py-2 bg-[#0F1829] text-white rounded text-sm inline-flex items-center gap-1.5 disabled:opacity-50">
              {isRegenerating ? <Loader2 size={14} className="animate-spin" /> : <RefreshCcw size={14} />}
              {isRegenerating ? "A processar…" : "Regenerar agora"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
